import type { UserAccessDecision } from "@/features/auth/types";
import type { SubscriptionStatusResponse } from "@/types";

const VALID_LEGACY_APP_STATUSES = new Set([
  "freemium",
  "monthly",
  "yearly",
  "cancelled",
  "cancelling_at_period_end",
  "past_due",
]);

function isLegacySubscriptionExpired(
  subscriptionStatus: SubscriptionStatusResponse | null | undefined
): boolean {
  return (
    subscriptionStatus?.subscription_status === "expired" ||
    subscriptionStatus?.subscription_status === "past_due" ||
    (subscriptionStatus?.subscription_status === "freemium" &&
      subscriptionStatus.subscription_trial_remaining === 0)
  );
}

function hasValidLegacyAppStatus(
  subscriptionStatus: SubscriptionStatusResponse | null | undefined
): boolean {
  const status = subscriptionStatus?.subscription_status;

  return !!status && VALID_LEGACY_APP_STATUSES.has(status);
}

export function hasAccessDecision(
  access: UserAccessDecision | null | undefined
): access is UserAccessDecision {
  return (
    !!access &&
    typeof access.can_use_app === "boolean" &&
    typeof access.can_access_chat === "boolean" &&
    typeof access.requires_subscription === "boolean"
  );
}

export function shouldBlockPersonalQuestionnaire(
  access: UserAccessDecision | null | undefined
): boolean {
  return hasAccessDecision(access) && access.source === "organization";
}

export function canUseAppFromAccess(
  access: UserAccessDecision | null | undefined
): boolean | undefined {
  if (!hasAccessDecision(access)) {
    return undefined;
  }

  return access.can_use_app === true;
}

export function canAccessChatFromAccess(
  access: UserAccessDecision | null | undefined
): boolean | undefined {
  if (!hasAccessDecision(access)) {
    return undefined;
  }

  return access.can_access_chat === true;
}

export function shouldShowPricingFromAccess(
  access: UserAccessDecision | null | undefined
): boolean | undefined {
  if (!hasAccessDecision(access)) {
    return undefined;
  }

  return (
    access.reason === "subscription_required" &&
    access.requires_subscription === true
  );
}

export function shouldShowAccessDeniedFromAccess(
  access: UserAccessDecision | null | undefined
): boolean | undefined {
  if (!hasAccessDecision(access)) {
    return undefined;
  }

  if (shouldShowPricingFromAccess(access)) {
    return false;
  }

  if (access.can_use_app || access.can_access_chat) {
    return false;
  }

  return access.requires_subscription === false;
}

export function legacySubscriptionAllowsApp(
  subscriptionStatus: SubscriptionStatusResponse | null | undefined
): boolean {
  if (!subscriptionStatus || isLegacySubscriptionExpired(subscriptionStatus)) {
    return false;
  }

  return hasValidLegacyAppStatus(subscriptionStatus);
}

export function legacySubscriptionAllowsChat(
  subscriptionStatus: SubscriptionStatusResponse | null | undefined
): boolean {
  return subscriptionStatus?.can_access_chat === true;
}

export function legacySubscriptionRequiresPricing(
  subscriptionStatus: SubscriptionStatusResponse | null | undefined
): boolean {
  if (!subscriptionStatus) {
    return true;
  }

  if (isLegacySubscriptionExpired(subscriptionStatus)) {
    return false;
  }

  return !hasValidLegacyAppStatus(subscriptionStatus);
}
